<template>
  <div class="pt-6 pb-10">
    <MessageBanner
      v-if="bannerContent"
      :headline="bannerContent.headline"
      :message="bannerContent.message"
      :type="bannerContent.type"
      class="mb-6"
      :data-testid="bannerContent.testId"
    >
      <button
        v-if="!review.manualReminderDisabled"
        class="btn-outline-neutral-xs"
        data-testid="reviews-send-reminder"
        @click="$emit('send-reminder')"
      >
        {{ t('Reviews.agency.view.actions.sendReminder') }}
        <BellIcon class="lucide-icon-s" />
      </button>
    </MessageBanner>
    <div class="grid grid-cols-3 items-start gap-6">
      <ReviewOverview class="col-span-3 px-4 desktop:col-span-1" :review="card.overview" />
      <div class="col-span-3 px-4 desktop:col-span-2">
        <ReviewQuote
          v-if="card.statement"
          :answer="card.statement"
          :question="t('Reviews.agency.reviews.statementQuestion', { agency: agencyTitle })"
          variant="neutral"
        />
        <ReplyItem
          v-if="!!reviewReply"
          :can-reply="true"
          :entity-title="agencyTitle"
          :review-reply="reviewReply"
          @delete-reply="$emit('delete-reply')"
          @edit-reply="$emit('edit-reply')"
        />
        <AgencyRemindersHistory class="mt-6" :author-name="review.reviewerName || undefined" :reminders="review.reminders ?? []" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { MessageBanner } from '@ramp106/omrjs-core-ui'
import { BellIcon } from '@ramp106/omrjs-core-ui/icons'
import { ReviewOverview, ReviewQuote } from '@ramp106/omrjs-reviews-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { UniversalReviewDetail } from '@/components/ReviewsAgenciesManagements/Reviews/types'
import ReplyItem from '@/components/ReviewsCommon/ReviewReply/ReplyItem.vue'
import { mapUniversalReviewToCardProps, UniversalReviewStates } from '@/gql/reviews'

import AgencyRemindersHistory from './AgencyRemindersHistory.vue'

const props = defineProps<{
  review: UniversalReviewDetail
  agencyTitle: string
}>()

defineEmits(['edit-reply', 'delete-reply', 'send-reminder'])

const { t } = useI18n()

const card = computed(() => mapUniversalReviewToCardProps(props.review, props.agencyTitle))

const bannerContent = computed(() => {
  switch (props.review.currentStatus) {
    case UniversalReviewStates.Rejected:
      return {
        testId: 'agency-review-rejected-banner',
        headline: t('Reviews.agency.review.rejected.messageBanner.headline'),
        message: t('Reviews.agency.review.rejected.reason.invalid'),
        type: 'error' as const,
      }
    case UniversalReviewStates.Drafted:
    case UniversalReviewStates.Stale:
      if (props.review.manualReminderDisabled) {
        return {
          testId: 'agency-review-reminder-banner',
          headline: t('Reviews.agency.review.draftedOrStale.blockedHeadline'),
          message: t('Reviews.agency.review.draftedOrStale.blockedMessage', {
            authorName: props.review.reviewerName || t('Reviews.agency.reviews.table.unknownAuthor'),
          }),
          type: 'info' as const,
        }
      }
      return {
        testId: 'agency-review-reminder-banner',
        headline: t('Reviews.agency.review.draftedOrStale.headline'),
        message: t('Reviews.agency.review.draftedOrStale.message', {
          authorName: props.review.reviewerName || t('Reviews.agency.reviews.table.unknownAuthor'),
        }),
        type: 'info' as const,
      }
    default:
      return null
  }
})

const reviewReply = computed(() => props.review.reviewReply || null)
</script>
